<template>
    <ExpandableMenuContainer :position="menuPosition" :fixed="true">
        <div class="flex items-end gap-1.5 sm:gap-2 md:gap-4">
            <KeyboardNavigation />
            <div class="flex flex-col items-center gap-1 md:gap-2">
                <!-- Zoom In (+) -->
                <div 
                    class="p-1 cursor-pointer transition-colors duration-200 group"
                    @click="handleZoomIn"
                    :class="{ 'opacity-50 cursor-not-allowed': !canZoomIn }"
                >
                    <Tooltip :text="$t('tooltip.zoomIn') || 'Zoom In'" position="left">
                        <div class="flex items-center justify-center w-full h-full">
                            <div class="relative rounded-full bg-[#ffffff] dark:bg-gray-800 p-2 md:p-3 shadow-md border border-gray-200 dark:border-gray-600 group-hover:text-white group-hover:bg-gradient-to-r group-hover:from-indigo-500 group-hover:to-purple-500 dark:group-hover:bg-gray-700 group-hover:border-transparent transition-colors cursor-pointer duration-150 flex items-center justify-center">
                                <MagnifyingGlassPlusIcon class="md:w-5 md:h-5 w-4 h-4" />
                            </div>
                        </div>
                    </Tooltip>
                </div>

                <!-- Zoom Out (-) -->
                <div 
                    class="p-1 cursor-pointer transition-colors duration-200 group"
                    @click="handleZoomOut"
                    :class="{ 'opacity-50 cursor-not-allowed': !canZoomOut }"
                >
                    <Tooltip :text="$t('tooltip.zoomOut') || 'Zoom Out'" position="left">
                        <div class="flex items-center justify-center w-full h-full">
                            <div class="relative rounded-full bg-[#ffffff] dark:bg-gray-800 p-2 md:p-3 shadow-md border border-gray-200 dark:border-gray-600 group-hover:text-white group-hover:bg-gradient-to-r group-hover:from-indigo-500 group-hover:to-purple-500 dark:group-hover:bg-gray-700 group-hover:border-transparent transition-colors cursor-pointer duration-150 flex items-center justify-center">
                                <MagnifyingGlassMinusIcon class="md:w-5 md:h-5 w-4 h-4" />
                            </div>
                        </div>
                    </Tooltip>
                </div>

                <!-- Menu Trigger Button -->
                <div class="p-1">
                    <ExpandableMenuList 
                        :menus="filteredMenus" 
                        :footer="footer" 
                        :menu-position="menuPosition"
                        @favorite-change="updateFavorite"
                    />
                </div>
            </div>
        </div>
    </ExpandableMenuContainer>
</template>

<script setup>
import ExpandableMenuContainer from '@/components/common/menus/ExpandableMenuContainer.vue'
import ExpandableMenuList from '@/components/common/menus/ExpandableMenuList.vue'
import KeyboardNavigation from '@/components/common/KeyboardNavigation.vue'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/vue/24/outline'
import { menuList, menuListFooter, listAccessMenusRestricted } from '@/components/common/menus/menus.js'
import zoomStore from '@/store/zoom.store'
import { appEvents } from '@/store/app.events'
import { ref, defineEmits, defineProps, computed } from 'vue';

const MAX_ZOOM_VALUE = 2
const MIN_ZOOM_VALUE = 0.5

const canZoomIn = computed(() => {
    return (zoomStore.state.zoom || 1) < MAX_ZOOM_VALUE
})

const canZoomOut = computed(() => {
    return (zoomStore.state.zoom || 1) > MIN_ZOOM_VALUE
})

const handleZoomIn = () => {
    if (!canZoomIn.value) return
    appEvents.emit('zoomIn')
}

const handleZoomOut = () => {
    if (!canZoomOut.value) return
    appEvents.emit('zoomOut')
}

const props = defineProps({
    allowAccess: {
        type: Boolean,
        default: false
    }
})
const menuPosition = ref('bottom-right');

const footer = ref(menuListFooter);
const emit = defineEmits(['updateFavorite']);

const filteredMenus = computed(() => {
    if (props.allowAccess) {
        return menuList;
    }
    return menuList.filter(menu => !listAccessMenusRestricted.includes(menu.id));
})


const updateFavorite = (menu) => {
    emit('updateFavorite', menu);
}
</script>